import { Link } from "react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nosible/shared/components/ui/table";
import { Badge } from "@nosible/shared/components/ui/badge";
import { Button } from "@nosible/shared/components/ui/button";
import { trpc } from "@/admin/lib/trpc";
import { Webhook, RefreshCw, Loader2 } from "lucide-react";

type WebhookEvent = {
  id: string;
  stripeEventId: string;
  eventType: string;
  processed: boolean;
  processedAt: string | null;
  error: string | null;
  createdAt: string;
};

const formatDate = (date: Date | string | null) => {
  if (!date) return "—";
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleString();
};

export function StripeWebhooks() {
  const {
    data: webhookEvents,
    isLoading: eventsLoading,
    refetch: refetchEvents,
  } = trpc.admin.getStripeWebhookEvents.useQuery({ limit: 20 });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Webhook className="h-5 w-5" />
          Recent Webhook Events
        </CardTitle>
        <CardDescription>Last 20 webhook events received from Stripe</CardDescription>
      </CardHeader>
      <CardContent>
        {eventsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-chromium-mist" />
          </div>
        ) : !webhookEvents || webhookEvents.length === 0 ? (
          <div className="text-center py-8 text-chromium-mist">
            <Webhook className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No webhook events recorded yet</p>
            <p className="text-sm mt-2">Events will appear here when Stripe sends webhooks</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Event Type</TableHead>
                <TableHead>Stripe Event ID</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Processed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(webhookEvents as WebhookEvent[]).map((event) => (
                <TableRow key={event.id}>
                  <TableCell className="font-mono text-sm">
                    <Link
                      to={`/stripe/events/${event.id}`}
                      className="text-silica hover:text-neon-green transition-colors"
                    >
                      {event.eventType}
                    </Link>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-chromium-mist">
                    {event.stripeEventId}
                  </TableCell>
                  <TableCell>
                    {event.processed ? (
                      event.error ? (
                        <Badge variant="outline" className="text-red-400 border-red-400">
                          Error
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-neon-green border-neon-green">
                          Processed
                        </Badge>
                      )
                    ) : (
                      <Badge variant="secondary">Pending</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-chromium-mist">
                    {formatDate(event.createdAt)}
                  </TableCell>
                  <TableCell className="text-sm text-chromium-mist">
                    {formatDate(event.processedAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <div className="mt-4 pt-4 border-t border-slate-core">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetchEvents()}
            disabled={eventsLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${eventsLoading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
